import React, { useMemo } from "react";
import { Box, Button, Divider, Paper, SvgIcon, Typography } from "@mui/material";
import * as d3 from "d3";
import { useNavigate } from "react-router-dom";
import LayoutMapping, { useLayoutMappingMetrics } from "../components/LayoutMapping";
import WorkspaceSidebar from "../components/WorkspaceSidebar";
import SellerMessageBubble from "../components/SellerMessageBubble";
import { ARAGON_COLORS, ARAGON_FONTS } from "../theme/AragonTheme";

function ArrowIcon(props) {
  return (
    <SvgIcon {...props} viewBox="0 0 24 24">
      <path d="M5 12h13m-5-5 5 5-5 5" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
    </SvgIcon>
  );
}
function DriverIcon({ negative, ...props }) {
  return (
    <SvgIcon {...props} viewBox="0 0 24 24">
      <circle cx="12" cy="12" r="8" fill="none" stroke="currentColor" strokeWidth="1.4" />
      <path d={negative ? "M8 12h8" : "M8 12h8M12 8v8"} fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </SvgIcon>
  );
}
function BuyerIcon(props) {
  return (
    <SvgIcon {...props} viewBox="0 0 24 24">
      <circle cx="9" cy="9" r="3" fill="none" stroke="currentColor" strokeWidth="1.5" />
      <path d="M4 19c.5-3.5 2.2-5 5-5s4.5 1.5 5 5m2-9h4m-2-2v4" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </SvgIcon>
  );
}
function WarningIcon(props) {
  return (
    <SvgIcon {...props} viewBox="0 0 24 24">
      <path d="m12 4 8 15H4L12 4Zm0 5v5m0 2.5v.5" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </SvgIcon>
  );
}

const MOCK_FINAL = {
  low: 23.4,
  central: 27.4,
  high: 31.3,
  unit: "B",
  currency: "VND",
};

const VALUE_DRIVERS = {
  strengths: [
    { title: "Recurring revenue %", detail: "68% subscription / repeat" },
    { title: "EBITDA margin", detail: "22.7% · above sector median" },
    { title: "Customer retention", detail: "Stable repeat demand profile" },
    { title: "Operating leverage", detail: "Margin expands with scale" },
  ],
  weaknesses: [
    { title: "Customer concentration", detail: "Top 3 / 43% of revenue" },
    { title: "Owner dependency", detail: "Founder still in daily operations" },
    { title: "Supplier concentration", detail: "Limited alternate sourcing" },
    { title: "Working capital intensity", detail: "Cash conversion remains elevated" },
  ],
};

function formatBillions(value, unit) {
  return `${value.toFixed(1)}${unit}`;
}

function ValuationHero({ metrics }) {
  const width = metrics.contentWidth;
  const local = useMemo(() => {
    const railWidth = Math.round(d3.scaleLinear().domain([620, 840]).range([460, 650]).clamp(true)(width));
    const heroHeight = Math.round(d3.scaleLinear().domain([620, 840]).range([150, 190]).clamp(true)(width));
    const valueSize = Math.round(d3.scaleLinear().domain([620, 840]).range([34, 44]).clamp(true)(width));
    const metaSize = Math.round(d3.scaleLinear().domain([620, 840]).range([9, 11]).clamp(true)(width));
    const railLabelSize = Math.round(d3.scaleLinear().domain([620, 840]).range([8, 10]).clamp(true)(width));
    const railValueSize = Math.round(d3.scaleLinear().domain([620, 840]).range([11, 14]).clamp(true)(width));
    return { railWidth, heroHeight, valueSize, metaSize, railLabelSize, railValueSize };
  }, [width]);
  const scale = useMemo(() => d3.scaleLinear().domain([MOCK_FINAL.low, MOCK_FINAL.high]).range([0, local.railWidth]).clamp(true), [local.railWidth]);
  const centralX = scale(MOCK_FINAL.central);
  const bandStart = Math.max(0, centralX - Math.round(local.railWidth * 0.17));
  const bandWidth = Math.min(local.railWidth - bandStart, Math.round(local.railWidth * 0.34));
  return (
    <Box sx={{ mt: `${metrics.cardGap + 10}px`, height: `${local.heroHeight}px`, borderRadius: `${metrics.cardRadius + 1}px`, overflow: "hidden", background: "linear-gradient(110deg, #071847 0%, #162253 58%, #3B374F 100%)", color: "#fff", px: `${metrics.cardPadding + 5}px`, py: `${metrics.cardPadding + 1}px`, boxSizing: "border-box", display: "flex", flexDirection: "column" }}>
      <Typography sx={{ fontFamily: ARAGON_FONTS.secondary, fontSize: `${metrics.eyebrowSize}px`, letterSpacing: "0.16em", textTransform: "uppercase", color: "#D6B585", fontWeight: 700 }}>Equity value · Banker validation pending</Typography>
      <Typography sx={{ mt: "14px", fontFamily: 'Georgia, "Times New Roman", serif', fontSize: `${local.valueSize}px`, lineHeight: 1, letterSpacing: "-0.025em" }}>
        {formatBillions(MOCK_FINAL.low, MOCK_FINAL.unit)} <Box component="span" sx={{ color: "#D6B585" }}>–</Box> {formatBillions(MOCK_FINAL.high, MOCK_FINAL.unit)}
      </Typography>
      <Typography sx={{ mt: "7px", fontFamily: ARAGON_FONTS.secondary, fontSize: `${local.metaSize}px`, letterSpacing: "0.04em", color: "rgba(255,255,255,0.68)" }}>Centre {formatBillions(MOCK_FINAL.central, MOCK_FINAL.unit)} · Confidence: indicative</Typography>
      <Box sx={{ mt: "auto", width: `${local.railWidth}px`, maxWidth: "100%" }}>
        <Box sx={{ position: "relative", height: "5px", borderRadius: "5px", bgcolor: "rgba(255,255,255,0.13)" }}>
          <Box sx={{ position: "absolute", left: `${bandStart}px`, width: `${bandWidth}px`, height: "5px", borderRadius: "5px", bgcolor: "#C89B62" }} />
          <Box sx={{ position: "absolute", left: `${centralX}px`, top: "-2px", width: "1px", height: "9px", bgcolor: "#fff" }} />
        </Box>
        <Box sx={{ mt: "7px", position: "relative", height: `${local.railLabelSize + local.railValueSize + 8}px`, color: "rgba(255,255,255,0.62)" }}>
          <Box sx={{ position: "absolute", left: 0, top: 0, textAlign: "left" }}>
            <Typography sx={{ fontFamily: ARAGON_FONTS.secondary, fontSize: `${local.railLabelSize}px`, lineHeight: 1, textTransform: "uppercase", letterSpacing: "0.11em", color: "rgba(255,255,255,0.48)" }}>Low</Typography>
            <Box component="span" sx={{ display: "block", mt: "4px", fontSize: `${local.railValueSize}px`, lineHeight: 1, color: "rgba(255,255,255,0.76)" }}>{formatBillions(MOCK_FINAL.low, MOCK_FINAL.unit)}</Box>
          </Box>
          <Box sx={{ position: "absolute", left: `${centralX}px`, top: 0, transform: "translateX(-50%)", textAlign: "center" }}>
            <Typography sx={{ fontFamily: ARAGON_FONTS.secondary, fontSize: `${local.railLabelSize}px`, lineHeight: 1, textTransform: "uppercase", letterSpacing: "0.11em", color: "rgba(255,255,255,0.62)" }}>Centre</Typography>
            <Box component="span" sx={{ display: "block", mt: "4px", fontSize: `${local.railValueSize}px`, lineHeight: 1, color: "#FFFFFF" }}>{formatBillions(MOCK_FINAL.central, MOCK_FINAL.unit)}</Box>
          </Box>
          <Box sx={{ position: "absolute", right: 0, top: 0, textAlign: "right" }}>
            <Typography sx={{ fontFamily: ARAGON_FONTS.secondary, fontSize: `${local.railLabelSize}px`, lineHeight: 1, textTransform: "uppercase", letterSpacing: "0.11em", color: "rgba(255,255,255,0.48)" }}>High</Typography>
            <Box component="span" sx={{ display: "block", mt: "4px", fontSize: `${local.railValueSize}px`, lineHeight: 1, color: "rgba(255,255,255,0.76)" }}>{formatBillions(MOCK_FINAL.high, MOCK_FINAL.unit)}</Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

function DriverColumn({ title, items, negative, metrics }) {
  const labelSize = Math.max(10, metrics.bodySize - 1);
  const detailSize = Math.max(8, metrics.bodySize - 3);
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ fontFamily: ARAGON_FONTS.secondary, fontSize: `${labelSize}px`, fontWeight: 700, color: ARAGON_COLORS.navy }}>{title}</Typography>
      <Box sx={{ mt: "13px", display: "flex", flexDirection: "column", gap: "13px" }}>
        {items.map((item) => (
          <Box key={item.title} sx={{ display: "grid", gridTemplateColumns: "21px minmax(0,1fr)", gap: "9px" }}>
            <Box sx={{ width: "20px", height: "20px", borderRadius: "50%", bgcolor: negative ? "#F9DADF" : "#E5E9F3", color: negative ? "#A92F48" : ARAGON_COLORS.navy, display: "grid", placeItems: "center" }}><DriverIcon negative={negative} sx={{ fontSize: "13px" }} /></Box>
            <Box>
              <Typography sx={{ fontFamily: ARAGON_FONTS.secondary, fontSize: `${labelSize}px`, lineHeight: 1.22, fontWeight: 700, color: ARAGON_COLORS.navy }}>{item.title}</Typography>
              <Typography sx={{ mt: "3px", fontFamily: ARAGON_FONTS.secondary, fontSize: `${detailSize}px`, lineHeight: 1.4, color: ARAGON_COLORS.steel }}>{item.detail}</Typography>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

function ValuationContent(metrics) {
  const navigate = useNavigate();
  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "18px" }}>
        <Box>
          <Typography sx={{ fontFamily: ARAGON_FONTS.secondary, fontSize: `${metrics.eyebrowSize}px`, letterSpacing: "0.15em", textTransform: "uppercase", color: ARAGON_COLORS.burgundy, fontWeight: 700 }}>Live deal valuation</Typography>
          <Typography sx={{ mt: "6px", fontFamily: ARAGON_FONTS.primary, fontSize: `${metrics.titleSize}px`, lineHeight: 1.04, color: ARAGON_COLORS.navy }}>Valuation dashboard</Typography>
          <Typography sx={{ mt: "6px", fontFamily: ARAGON_FONTS.secondary, fontSize: `${metrics.bodySize}px`, color: ARAGON_COLORS.steel }}>Updated view of your deal value as buyer feedback comes in.</Typography>
        </Box>
        <Button onClick={() => navigate("/seller/deal-room")} endIcon={<ArrowIcon sx={{ fontSize: "14px !important" }} />} sx={{ mt: "1px", height: "34px", px: "15px", bgcolor: ARAGON_COLORS.navy, color: "#fff", borderRadius: "7px", fontFamily: ARAGON_FONTS.secondary, fontSize: `${Math.max(9, metrics.bodySize - 2)}px`, whiteSpace: "nowrap", "&:hover": { bgcolor: "#142455" } }}>Open deal room</Button>
      </Box>
      <ValuationHero metrics={metrics} />
      <Paper elevation={0} sx={{ mt: `${metrics.cardGap}px`, border: "1px solid rgba(13,25,65,0.11)", borderRadius: `${metrics.cardRadius}px`, bgcolor: "#FFFEFC", p: `${metrics.cardPadding}px` }}>
        <Typography sx={{ fontFamily: ARAGON_FONTS.secondary, fontSize: `${metrics.eyebrowSize}px`, letterSpacing: "0.16em", textTransform: "uppercase", color: ARAGON_COLORS.burgundy, fontWeight: 700 }}>Value drivers</Typography>
        <Typography sx={{ mt: "4px", fontFamily: ARAGON_FONTS.primary, fontSize: `${metrics.sectionTitleSize}px`, color: ARAGON_COLORS.navy }}>What moves the number</Typography>
        <Box sx={{ mt: "18px", display: "grid", gridTemplateColumns: metrics.compact ? "1fr" : "1fr 1px 1fr", gap: `${metrics.cardPadding + 2}px`, alignItems: "stretch" }}>
          <DriverColumn title="Plus point" items={VALUE_DRIVERS.strengths} metrics={metrics} />
          {!metrics.compact && <Divider orientation="vertical" flexItem sx={{ borderColor: "rgba(13,25,65,0.09)" }} />}
          <DriverColumn title="Minus point" items={VALUE_DRIVERS.weaknesses} negative metrics={metrics} />
        </Box>
      </Paper>
      <Paper elevation={0} sx={{ mt: `${metrics.cardGap}px`, border: "1px solid rgba(13,25,65,0.11)", borderRadius: `${metrics.cardRadius}px`, bgcolor: "#FFFEFC", px: `${metrics.cardPadding}px`, py: `${metrics.cardPadding - 1}px`, display: "flex", alignItems: "center", justifyContent: "space-between", gap: "22px" }}>
        <Box>
          <Typography sx={{ fontFamily: ARAGON_FONTS.secondary, fontSize: `${metrics.eyebrowSize}px`, letterSpacing: "0.15em", textTransform: "uppercase", color: ARAGON_COLORS.burgundy, fontWeight: 700 }}>Active buyer pool</Typography>
          <Typography sx={{ mt: "6px", fontFamily: ARAGON_FONTS.primary, fontSize: `${metrics.sectionTitleSize}px`, color: "#C48A4B" }}>4 engaged buyers in your range</Typography>
          <Typography sx={{ mt: "5px", fontFamily: ARAGON_FONTS.secondary, fontSize: `${Math.max(9, metrics.bodySize - 2)}px`, color: ARAGON_COLORS.steel }}>All under NDA and reviewing the data room.</Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: "8px", color: ARAGON_COLORS.navy }}><BuyerIcon sx={{ fontSize: "21px", color: "#C48A4B" }} /><Typography sx={{ fontFamily: 'Georgia, "Times New Roman", serif', fontSize: "27px" }}>4</Typography></Box>
      </Paper>
      <Box sx={{ mt: `${metrics.cardGap}px`, border: "1px solid rgba(196,138,75,0.22)", borderRadius: `${metrics.cardRadius}px`, bgcolor: "rgba(224,217,199,0.20)", px: `${metrics.cardPadding}px`, py: "12px", display: "grid", gridTemplateColumns: "20px 1fr", gap: "9px", alignItems: "start" }}>
        <WarningIcon sx={{ fontSize: "16px", color: "#C48A4B", mt: "1px" }} />
        <Typography sx={{ fontFamily: ARAGON_FONTS.secondary, fontSize: `${Math.max(8, metrics.bodySize - 3)}px`, lineHeight: 1.55, color: ARAGON_COLORS.steel }}>This is a live indicative valuation. It updates as buyer feedback, offers, and diligence progress come in. Your advisor will finalize pricing before any binding agreement.</Typography>
      </Box>
    </Box>
  );
}

function useValuationLayoutMetrics() {
  const shared = useLayoutMappingMetrics();
  return useMemo(() => {
    const width = shared.viewport.width;
    const height = shared.viewport.height;
    return {
      ...shared,
      contentWidth: Math.round(d3.scaleLinear().domain([1024, 1920]).range([620, 840]).clamp(true)(width)),
      contentTop: Math.round(d3.scaleLinear().domain([620, 1080]).range([30, 42]).clamp(true)(height)),
    };
  }, [shared]);
}

export default function ValuationResult() {
  const metrics = useValuationLayoutMetrics();
  return (
    <LayoutMapping
      metrics={metrics}
      sidebar={<WorkspaceSidebar metrics={metrics} />}
      floatingUtility={<SellerMessageBubble metrics={metrics} />}
    >
      <ValuationContent {...metrics} />
    </LayoutMapping>
  );
}
